/**
 * SessionTableEditor
 * Inline editable table for managing sessions
 * Feature: User Management (002-user-management) - Phase 3
 */

import React, { useState, useEffect } from 'react';
import AdminApiService from '../services/AdminApiService.js';
import { imageValidator } from '../utils/imageValidator.js';
import { useCategories } from '../hooks/useCategories';
import './SessionTableEditor.css';

const SessionTableEditor = ({ 
  sessions, 
  onUpdate, 
  onDelete, 
  onPublish, 
  onUnpublish,
  onAdd,
  loading = false,
  error = null 
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSession, setNewSession] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'timeCET', direction: 'asc' });
  const [uploadingPictureId, setUploadingPictureId] = useState(null);
  const [showUploadRequirements, setShowUploadRequirements] = useState(false);
  const [selectedSessionForUpload, setSelectedSessionForUpload] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationModal, setNotificationModal] = useState({ type: '', title: '', message: '', onConfirm: null });

  // Debug: Log sessions to see if presenterPictureUrl is present
  useEffect(() => {
    if (sessions && sessions.length > 0) {
      const sessionsWithPics = sessions.filter(s => s.presenterPictureUrl);
      console.log('Sessions with presenterPictureUrl:', sessionsWithPics.length);
      sessionsWithPics.forEach(s => {
        console.log(`- ${s.name}: ${s.presenterPictureUrl}`);
      });
    }
  }, [sessions]);

  const { byCode: catColors, list: catList } = useCategories();

  const getCategoryDisplay = (categoryCode) => {
    const entry = catColors[categoryCode];
    return entry
      ? { code: categoryCode, name: entry.name, color: entry.color }
      : { code: categoryCode, name: categoryCode, color: '#808080' };
  };

  useEffect(() => {
    // Cancel editing if sessions reload
    if (loading) {
      setEditingId(null);
      setShowAddForm(false);
    }
  }, [loading]);

  const handleEdit = (session) => {
    setEditingId(session.id);
    setEditValues({
      ...session,
      clientUpdatedAt: session.updatedAt // Store for conflict detection
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const isValidDate = (val) => {
    if (!val) return true; // required check handled separately
    return /^\d{2}-\d{2}-\d{4}$/.test(val.trim());
  };

  const isValidTime = (val) => {
    if (!val) return true;
    return /^\d{2}:\d{2}-\d{2}:\d{2}$/.test(val.trim());
  };

  const isValidSabaLink = (url) => {
    if (!url || url.trim() === '') return true;
    const specialValues = ['tbd', 'tba', 'n/a', 'na', 'pending', 'lab is full', 'view recording', 'save your seat'];
    if (specialValues.includes(url.trim().toLowerCase())) return true;
    try {
      const parsed = new URL(url.trim());
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSaveEdit = async () => {
    if (!editValues.name || !editValues.presenter || !editValues.timeCET || !editValues.date) {
      setNotificationModal({
        type: 'error',
        title: '⚠️ Required Fields Missing',
        message: 'Please fill in all required fields:\n\n• Name\n• Presenter\n• Time CET\n• Date',
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }
    const linkRequiredTexts = ['save your seat', 'lab is full', 'view recording'];
    if (linkRequiredTexts.includes((editValues.buttonText || '').trim().toLowerCase()) && !editValues.sabaLink?.trim()) {
      setNotificationModal({
        type: 'error',
        title: '⚠️ Link Required',
        message: `A link is required when button text is "${editValues.buttonText}".\n\nPlease enter a valid URL starting with http:// or https://.`,
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }
    if (!isValidDate(editValues.date)) {
      setNotificationModal({
        type: 'error',
        title: '⚠️ Invalid Date',
        message: `"${editValues.date}" is not a valid date.\n\nExpected format: DD-MM-YYYY (e.g. 05-11-2025)`,
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }
    if (!isValidTime(editValues.timeCET)) {
      setNotificationModal({
        type: 'error',
        title: '⚠️ Invalid Time',
        message: `"${editValues.timeCET}" is not a valid time range.\n\nExpected format: HH:MM-HH:MM (e.g. 10:00-11:00)`,
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }
    if (!isValidSabaLink(editValues.sabaLink)) {
      setNotificationModal({
        type: 'error',
        title: '⚠️ Invalid URL',
        message: `The link "${editValues.sabaLink}" is not a valid URL.\n\nPlease enter a URL starting with http:// or https://.`,
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }

    try {
      await onUpdate(editingId, editValues, editValues.clientUpdatedAt);
      setEditingId(null);
      setEditValues({});
    } catch (error) {
      console.error('Failed to save session:', error);
      setNotificationModal({
        type: 'error',
        title: '❌ Failed to Save',
        message: error.message || 'An unexpected error occurred while saving. Please try again.',
        onConfirm: null
      });
      setShowNotificationModal(true);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditValues(prev => ({ ...prev, [field]: value }));
  };

  const handleDelete = async (session) => {
    const confirmMessage = `Delete session "${session.name}"?\n\nThis action cannot be undone.`;
    if (window.confirm(confirmMessage)) {
      try {
        await onDelete(session.id);
      } catch (error) {
        console.error('Failed to delete session:', error);
      }
    }
  };

  const handlePublish = async (session) => {
    try {
      await onPublish(session.id);
    } catch (error) {
      console.error('Failed to publish session:', error);
    }
  };

  const handleUnpublish = async (session) => {
    try {
      await onUnpublish(session.id);
    } catch (error) {
      console.error('Failed to unpublish session:', error);
    }
  };

  const handlePictureUpload = async (sessionId, file) => {
    if (!file) return;

    // Validate image using imageValidator utility
    try {
      const validation = await imageValidator.validateImage(file);
      
      if (!validation.valid) {
        // Show validation errors
        const errorMessage = validation.errors.join('\n\n');
        setNotificationModal({
          type: 'error',
          title: '❌ Invalid Image',
          message: errorMessage,
          onConfirm: null
        });
        setShowNotificationModal(true);
        return;
      }
      
      // Show warnings if any (but continue with upload)
      if (validation.warnings.length > 0) {
        console.warn('Image validation warnings:', validation.warnings);
      }
    } catch (error) {
      console.error('Image validation error:', error);
      setNotificationModal({
        type: 'error',
        title: '❌ Validation Error',
        message: `Failed to validate image: ${error.message}`,
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }

    setUploadingPictureId(sessionId);
    
    try {
      const result = await AdminApiService.uploadPresenterPicture(sessionId, file);
      
      // Log success details
      const successLog = {
        timestamp: new Date().toISOString(),
        type: 'SUCCESS',
        sessionId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        result
      };
      console.log('Upload success:', successLog);
      
      // Update the session in the local state
      if (result.success && result.pictureUrl) {
        // Update the sessions array with the new picture URL
        const updatedSessions = sessions.map(s => 
          s.id === sessionId 
            ? { ...s, presenterPictureUrl: result.pictureUrl }
            : s
        );
        
        // Call parent's onUpdate to refresh the sessions
        if (onUpdate) {
          // Show success notification
          setNotificationModal({
            type: 'success',
            title: '✅ Success',
            message: 'Presenter picture uploaded successfully!',
            onConfirm: null
          });
          setShowNotificationModal(true);
          
          // Force a refresh by calling the parent's load function
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('refreshSessions'));
            setShowNotificationModal(false);
          }, 1500);
        }
      }
    } catch (error) {
      console.error('Upload error caught:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Failed to upload presenter picture.';
      let errorTitle = '❌ Upload Failed';
      
      if (error.message) {
        errorMessage = error.message;
        
        // Customize title based on error type
        if (error.message.includes('Aspect ratio')) {
          errorTitle = '❌ Invalid Image Dimensions';
        } else if (error.message.includes('dimensions')) {
          errorTitle = '❌ Image Too Small';
        } else if (error.message.includes('Network') || error.message.includes('Failed to fetch')) {
          errorTitle = '❌ Network Error';
        }
      }
      
      // Show user-friendly notification
      setNotificationModal({
        type: 'error',
        title: errorTitle,
        message: errorMessage,
        onConfirm: null
      });
      setShowNotificationModal(true);
    } finally {
      setUploadingPictureId(null);
    }
  };

  const handlePictureDelete = (sessionId, sessionName, pictureUrl = null) => {
    const message = pictureUrl 
      ? `Remove this presenter photo from "${sessionName}"?\n\nOther photos will remain.`
      : `Remove all presenter photos for "${sessionName}"?\n\nThis will restore the default placeholder icon.`;
    
    setNotificationModal({
      type: 'confirm',
      title: '🗑️ Remove Presenter Photo',
      message: message,
      onConfirm: async () => {
        setShowNotificationModal(false);
        try {
          await AdminApiService.deletePresenterPicture(sessionId, pictureUrl);
          
          // Show success notification
          setNotificationModal({
            type: 'success',
            title: '✅ Success',
            message: 'Presenter picture removed successfully!',
            onConfirm: null
          });
          setShowNotificationModal(true);
          
          // Dispatch refresh event to update the table
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('refreshSessions'));
            setShowNotificationModal(false);
          }, 1500);
        } catch (error) {
          console.error('Delete picture error:', error);
          setNotificationModal({
            type: 'error',
            title: '❌ Delete Failed',
            message: `Failed to remove picture: ${error.message}`,
            onConfirm: null
          });
          setShowNotificationModal(true);
        }
      }
    });
    setShowNotificationModal(true);
  };



  const triggerFileInput = (session) => {
    setSelectedSessionForUpload(session);
    setShowUploadRequirements(true);
  };

  const proceedWithUpload = () => {
    setShowUploadRequirements(false);
    const input = document.getElementById(`picture-input-${selectedSessionForUpload.id}`);
    if (input) {
      input.click();
    }
  };

  const cancelUpload = () => {
    setShowUploadRequirements(false);
    setSelectedSessionForUpload(null);
  };

  const handleAddSession = () => {
    setShowAddForm(true);
    setNewSession({
      name: '',
      presenter: '',
      category: 'BUSINESS_SKILLS',
      timeCET: '',
      date: '',
      description: '',
      guestSpeaker: null,
      recommendedSession: null,
      sabaLink: '',
      buttonText: 'Save Your Seat',
      status: 'draft'
    });
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewSession({});
  };

  const handleSaveAdd = async () => {
    if (!newSession.name || !newSession.presenter || !newSession.timeCET || !newSession.date) {
      setNotificationModal({
        type: 'error',
        title: '⚠️ Required Fields Missing',
        message: 'Please fill in all required fields:\n\n• Name\n• Presenter\n• Time CET\n• Date',
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }
    const linkRequiredTexts = ['save your seat', 'lab is full', 'view recording'];
    if (linkRequiredTexts.includes((newSession.buttonText || '').trim().toLowerCase()) && !newSession.sabaLink?.trim()) {
      setNotificationModal({
        type: 'error',
        title: '⚠️ Link Required',
        message: `A link is required when button text is "${newSession.buttonText}".\n\nPlease enter a valid URL starting with http:// or https://.`,
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }
    if (!isValidDate(newSession.date)) {
      setNotificationModal({
        type: 'error',
        title: '⚠️ Invalid Date',
        message: `"${newSession.date}" is not a valid date.\n\nExpected format: DD-MM-YYYY (e.g. 05-11-2025)`,
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }
    if (!isValidTime(newSession.timeCET)) {
      setNotificationModal({
        type: 'error',
        title: '⚠️ Invalid Time',
        message: `"${newSession.timeCET}" is not a valid time range.\n\nExpected format: HH:MM-HH:MM (e.g. 10:00-11:00)`,
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }
    if (!isValidSabaLink(newSession.sabaLink)) {
      setNotificationModal({
        type: 'error',
        title: '⚠️ Invalid URL',
        message: `The link "${newSession.sabaLink}" is not a valid URL.\n\nPlease enter a URL starting with http:// or https://.`,
        onConfirm: null
      });
      setShowNotificationModal(true);
      return;
    }

    try {
      await onAdd(newSession);
      setShowAddForm(false);
      setNewSession({});
    } catch (error) {
      console.error('Failed to add session:', error);
      setNotificationModal({
        type: 'error',
        title: '❌ Failed to Add Session',
        message: error.message || 'An unexpected error occurred while adding the session. Please try again.',
        onConfirm: null
      });
      setShowNotificationModal(true);
    }
  };

  const handleNewFieldChange = (field, value) => {
    setNewSession(prev => ({ ...prev, [field]: value }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedSessions = React.useMemo(() => {
    if (!sessions) return [];
    
    const sorted = [...sessions].sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      
      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return sorted;
  }, [sessions, sortConfig]);

  const renderCell = (session, field) => {
    const isEditing = editingId === session.id;
    const value = isEditing ? editValues[field] : session[field];

    if (!isEditing) {
      if (field === 'guestSpeaker' || field === 'recommendedSession') {
        return value === 'Yes' ? '✓' : '';
      }
      if (field === 'status') {
        return (
          <span className={`status-badge status-${value}`}>
            {value}
          </span>
        );
      }
      if (field === 'category') {
        const cat = getCategoryDisplay(value);
        return (
          <div className="category-display">
            <div className="category-box" style={{ background: cat.color }}></div>
            <span title={cat.name}>{cat.name}</span>
          </div>
        );
      }
      if (field === 'sabaLink') {
        return value ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="link-cell">
            Link
          </a>
        ) : '';
      }
      if (field === 'buttonText') {
        return value || 'Save Your Seat';
      }
      return value || '';
    }

    // Editing mode
    if (field === 'category') {
      return (
        <select
          value={value || (catList[0]?.code || '')}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          className="edit-input edit-select"
        >
          {catList.map(cat => (
            <option key={cat.code} value={cat.code}>{cat.name}</option>
          ))}
        </select>
      );
    }

    if (field === 'guestSpeaker' || field === 'recommendedSession') {
      return (
        <select
          value={value || ''}
          onChange={(e) => handleFieldChange(field, e.target.value || null)}
          className="edit-input edit-select"
        >
          <option value="">No</option>
          <option value="Yes">Yes</option>
        </select>
      );
    }

    if (field === 'buttonText') {
      return (
        <select
          value={value || 'Save Your Seat'}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          className="edit-input edit-select"
        >
          <option value="Save Your Seat">Save Your Seat</option>
          <option value="Lab is Full">Lab is Full</option>
          <option value="Postponed">Postponed</option>
          <option value="View Recording">View Recording</option>
          <option value="Coming Soon">Coming Soon</option>
        </select>
      );
    }

    if (field === 'status') {
      return (
        <select
          value={value || 'draft'}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          className="edit-input edit-select"
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      );
    }

    if (field === 'description') {
      return (
        <textarea
          value={value || ''}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          className="edit-input edit-textarea"
          rows={2}
        />
      );
    }

    if (field === 'date') {
      const dateInvalid = value && !isValidDate(value);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            className="edit-input"
            placeholder="DD-MM-YYYY"
            style={dateInvalid ? { borderColor: '#e53e3e', outline: '1px solid #e53e3e' } : {}}
          />
          {dateInvalid && (
            <span style={{ color: '#e53e3e', fontSize: '11px', lineHeight: 1.2 }}>
              Expected DD-MM-YYYY (e.g. 05-11-2025)
            </span>
          )}
        </div>
      );
    }

    if (field === 'timeCET') {
      const timeInvalid = value && !isValidTime(value);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            className="edit-input"
            placeholder="HH:MM-HH:MM"
            style={timeInvalid ? { borderColor: '#e53e3e', outline: '1px solid #e53e3e' } : {}}
          />
          {timeInvalid && (
            <span style={{ color: '#e53e3e', fontSize: '11px', lineHeight: 1.2 }}>
              Expected HH:MM-HH:MM (e.g. 10:00-11:00)
            </span>
          )}
        </div>
      );
    }

    if (field === 'sabaLink') {
      const urlInvalid = value && !isValidSabaLink(value);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            className="edit-input"
            placeholder="https://..."
            style={urlInvalid ? { borderColor: '#e53e3e', outline: '1px solid #e53e3e' } : {}}
          />
          {urlInvalid && (
            <span style={{ color: '#e53e3e', fontSize: '11px', lineHeight: 1.2 }}>
              Invalid URL — must start with https:// or http://
            </span>
          )}
        </div>
      );
    }

    return (
      <input
        type="text"
        value={value || ''}
        onChange={(e) => handleFieldChange(field, e.target.value)}
        className="edit-input"
      />
    );
  };

  const renderActions = (session) => {
    if (editingId === session.id) {
      return (
        <div className="action-buttons">
          <button onClick={handleSaveEdit} className="btn-save" title="Save">
            💾
          </button>
          <button onClick={handleCancelEdit} className="btn-cancel" title="Cancel">
            ✖
          </button>
        </div>
      );
    }

    return (
      <div className="action-buttons">
        <button onClick={() => handleEdit(session)} className="btn-edit" title="Edit">
          ✏️
        </button>
        {session.status === 'draft' && (
          <button onClick={() => handlePublish(session)} className="btn-publish" title="Publish">
            🚀
          </button>
        )}
        {session.status === 'published' && (
          <button onClick={() => handleUnpublish(session)} className="btn-unpublish" title="Unpublish">
            ⬇️
          </button>
        )}
        <button onClick={() => handleDelete(session)} className="btn-delete" title="Delete">
          🗑️
        </button>
      </div>
    );
  };

  if (error) {
    return (
      <div className="session-editor-error">
        <p>Error loading sessions: {error}</p>
      </div>
    );
  }

  return (
    <div className="session-table-editor">
      <div className="editor-header">
        <h3>Sessions ({sessions?.length || 0})</h3>
        <button onClick={handleAddSession} className="btn-add-session" disabled={loading || showAddForm}>
          + Add Session
        </button>
      </div>

      {loading && <div className="editor-loading">Loading sessions...</div>}

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')}>Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('presenter')}>Presenter {sortConfig.key === 'presenter' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
              <th>Photo</th>
              <th onClick={() => handleSort('category')}>Category {sortConfig.key === 'category' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('timeCET')}>Time CET {sortConfig.key === 'timeCET' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('date')}>Date {sortConfig.key === 'date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
              <th>Description</th>
              <th>Guest</th>
              <th>Recommended</th>
              <th>Link</th>
              <th onClick={() => handleSort('buttonText')}>Button Text {sortConfig.key === 'buttonText' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('status')}>Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {showAddForm && (
              <tr className="add-row">
                <td><input type="text" value={newSession.name || ''} onChange={(e) => handleNewFieldChange('name', e.target.value)} className="edit-input" placeholder="Session name *" /></td>
                <td><input type="text" value={newSession.presenter || ''} onChange={(e) => handleNewFieldChange('presenter', e.target.value)} className="edit-input" placeholder="Presenter *" /></td>
                <td className="picture-cell">—</td>
                <td>
                  <select value={newSession.category || (catList[0]?.code || '')} onChange={(e) => handleNewFieldChange('category', e.target.value)} className="edit-input edit-select">
                    {catList.map(cat => (<option key={cat.code} value={cat.code}>{cat.name}</option>))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    value={newSession.timeCET || ''}
                    onChange={(e) => handleNewFieldChange('timeCET', e.target.value)}
                    className="edit-input"
                    placeholder="HH:MM-HH:MM *"
                    style={newSession.timeCET && !isValidTime(newSession.timeCET) ? { borderColor: '#e53e3e', outline: '1px solid #e53e3e' } : {}}
                  />
                  {newSession.timeCET && !isValidTime(newSession.timeCET) && (
                    <div style={{ color: '#e53e3e', fontSize: '11px', lineHeight: 1.2, marginTop: 2 }}>
                      Expected HH:MM-HH:MM (e.g. 10:00-11:00)
                    </div>
                  )}
                </td>
                <td>
                  <input
                    type="text"
                    value={newSession.date || ''}
                    onChange={(e) => handleNewFieldChange('date', e.target.value)}
                    className="edit-input"
                    placeholder="DD-MM-YYYY *"
                    style={newSession.date && !isValidDate(newSession.date) ? { borderColor: '#e53e3e', outline: '1px solid #e53e3e' } : {}}
                  />
                  {newSession.date && !isValidDate(newSession.date) && (
                    <div style={{ color: '#e53e3e', fontSize: '11px', lineHeight: 1.2, marginTop: 2 }}>
                      Expected DD-MM-YYYY (e.g. 05-11-2025)
                    </div>
                  )}
                </td>
                <td><textarea value={newSession.description || ''} onChange={(e) => handleNewFieldChange('description', e.target.value)} className="edit-input edit-textarea" rows={2} /></td>
                <td>
                  <select value={newSession.guestSpeaker || ''} onChange={(e) => handleNewFieldChange('guestSpeaker', e.target.value || null)} className="edit-input edit-select">
                    <option value="">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </td>
                <td>
                  <select value={newSession.recommendedSession || ''} onChange={(e) => handleNewFieldChange('recommendedSession', e.target.value || null)} className="edit-input edit-select">
                    <option value="">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    value={newSession.sabaLink || ''}
                    onChange={(e) => handleNewFieldChange('sabaLink', e.target.value)}
                    className="edit-input"
                    placeholder="https://..."
                    style={newSession.sabaLink && !isValidSabaLink(newSession.sabaLink) ? { borderColor: '#e53e3e', outline: '1px solid #e53e3e' } : {}}
                  />
                  {newSession.sabaLink && !isValidSabaLink(newSession.sabaLink) && (
                    <div style={{ color: '#e53e3e', fontSize: '11px', lineHeight: 1.2, marginTop: 2 }}>
                      Invalid URL — must start with https:// or http://
                    </div>
                  )}
                </td>
                <td>
                  <select value={newSession.buttonText || 'Save Your Seat'} onChange={(e) => handleNewFieldChange('buttonText', e.target.value)} className="edit-input edit-select">
                    <option value="Save Your Seat">Save Your Seat</option>
                    <option value="Lab is Full">Lab is Full</option>
                    <option value="Postponed">Postponed</option>
                    <option value="View Recording">View Recording</option>
                    <option value="Coming Soon">Coming Soon</option>
                  </select>
                </td>
                <td>
                  <select value={newSession.status || 'draft'} onChange={(e) => handleNewFieldChange('status', e.target.value)} className="edit-input edit-select">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </td>
                <td>
                  <div className="action-buttons">
                    <button onClick={handleSaveAdd} className="btn-save" title="Save">💾</button>
                    <button onClick={handleCancelAdd} className="btn-cancel" title="Cancel">✖</button>
                  </div>
                </td>
              </tr>
            )}
            
            {sortedSessions.map(session => (
              <tr key={session.id} className={editingId === session.id ? 'editing-row' : ''}>
                <td>{renderCell(session, 'name')}</td>
                <td>{renderCell(session, 'presenter')}</td>
                <td className="picture-cell">
                  {(() => {
                    // Parse picture URLs (can be single string or JSON array)
                    let pictureUrls = [];
                    if (session.presenterPictureUrl) {
                      try {
                        pictureUrls = JSON.parse(session.presenterPictureUrl);
                        if (!Array.isArray(pictureUrls)) {
                          pictureUrls = [session.presenterPictureUrl];
                        }
                      } catch {
                        pictureUrls = [session.presenterPictureUrl];
                      }
                    }
                    
                    // Comma is always the separator between presenters
                    const presenters = (session.presenter || '').split(',').map(p => p.trim()).filter(p => p);
                    const presenterCount = presenters.length;
                    const filledSlots = pictureUrls.filter(url => url !== null && url !== undefined).length;
                    // 4+ presenters: single logo slot; 1-3: per-presenter slots
                    const isLogoMode = presenterCount > 3;
                    const canAddMore = isLogoMode ? filledSlots === 0 : filledSlots < presenterCount;
                    
                    return (
                      <div className="pictures-container">
                        {pictureUrls.map((url, index) => {
                          const presenterName = isLogoMode ? 'Session Logo' : (presenters[index] || `Presenter ${index + 1}`);
                          
                          if (url) {
                            return (
                              <div key={index} className="thumbnail-container" title={presenterName}>
                                <img 
                                  src={url.startsWith('http') ? url : `${url}`}
                                  alt={presenterName}
                                  className="presenter-thumbnail"
                                  title={`${presenterName} - Click to change`}
                                  onClick={() => triggerFileInput(session)}
                                />
                                <button
                                  className="btn-delete-picture"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePictureDelete(session.id, session.name, url);
                                  }}
                                  title={`Remove ${presenterName}`}
                                >
                                  ✖
                                </button>
                              </div>
                            );
                          }
                          return null;
                        })}
                        {canAddMore && (
                          <button 
                            className="btn-upload-picture"
                            onClick={() => triggerFileInput(session)}
                            disabled={uploadingPictureId === session.id}
                            title={isLogoMode ? 'Upload session logo' : (filledSlots === 0 ? 'Upload presenter photo' : `Add photo (${filledSlots}/${presenterCount})`)}
                          >
                            {uploadingPictureId === session.id ? '⏳' : '📷'}
                          </button>
                        )}
                        <input
                          type="file"
                          id={`picture-input-${session.id}`}
                          accept="image/jpeg,image/jpg,image/png"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handlePictureUpload(session.id, file);
                            }
                            e.target.value = ''; // Reset input
                          }}
                        />
                      </div>
                    );
                  })()}
                </td>
                <td>{renderCell(session, 'category')}</td>
                <td>{renderCell(session, 'timeCET')}</td>
                <td>{renderCell(session, 'date')}</td>
                <td>{renderCell(session, 'description')}</td>
                <td className="center-cell">{renderCell(session, 'guestSpeaker')}</td>
                <td className="center-cell">{renderCell(session, 'recommendedSession')}</td>
                <td>{renderCell(session, 'sabaLink')}</td>
                <td>{renderCell(session, 'buttonText')}</td>
                <td>{renderCell(session, 'status')}</td>
                <td>{renderActions(session)}</td>
              </tr>
            ))}
            
            {!loading && sortedSessions.length === 0 && !showAddForm && (
              <tr>
                <td colSpan="13" className="empty-state">
                  No sessions found. Import an Excel file or add a manual session.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Upload Requirements Modal */}
      {showUploadRequirements && (
        <div className="modal-overlay" onClick={cancelUpload}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📷 Presenter Picture Upload Requirements</h3>
              <button className="modal-close" onClick={cancelUpload}>✖</button>
            </div>
            <div className="modal-body">
              <p className="modal-intro">
                Uploading picture for: <strong>{selectedSessionForUpload?.presenter}</strong>
              </p>
              
              <div className="requirements-list">
                <div className="requirement-item validated">
                  <span className="requirement-icon">✅</span>
                  <div className="requirement-text">
                    <strong>Image Format:</strong> JPEG or PNG only
                    <div className="requirement-note">Will be validated before upload</div>
                  </div>
                </div>

                <div className="requirement-item validated">
                  <span className="requirement-icon">✅</span>
                  <div className="requirement-text">
                    <strong>File Size:</strong> Any size accepted (auto-compressed after upload)
                    <div className="requirement-note">Large images will be optimized automatically</div>
                  </div>
                </div>

                <div className="requirement-item validated">
                  <span className="requirement-icon">✅</span>
                  <div className="requirement-text">
                    <strong>Aspect Ratio:</strong> Any aspect ratio accepted (displayed as circle)
                    <div className="requirement-note">Images are cropped to fit circular display</div>
                  </div>
                </div>

                <div className="requirement-item info">
                  <span className="requirement-icon">💡</span>
                  <div className="requirement-text">
                    <strong>Filename Tip:</strong> Use presenter's name for automatic matching
                    <div className="requirement-note">Example: {selectedSessionForUpload?.presenter?.replace(/\s+/g, '_')}.jpg</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-modal-cancel" onClick={cancelUpload}>
                Cancel
              </button>
              <button className="btn-modal-proceed" onClick={proceedWithUpload}>
                Choose File
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Notification Modal */}
      {showNotificationModal && (
        <div className="modal-overlay" onClick={() => notificationModal.type !== 'confirm' && setShowNotificationModal(false)}>
          <div className={`modal-content notification-modal ${notificationModal.type}`} onClick={(e) => e.stopPropagation()}>
            <div className={`modal-header ${notificationModal.type}-header`}>
              <h3>{notificationModal.title}</h3>
              {notificationModal.type !== 'confirm' && (
                <button className="modal-close" onClick={() => setShowNotificationModal(false)}>✖</button>
              )}
            </div>
            <div className="modal-body">
              <p className="notification-message">{notificationModal.message}</p>
            </div>
            <div className="modal-footer">
              {notificationModal.type === 'confirm' ? (
                <>
                  <button className="btn-modal-cancel" onClick={() => setShowNotificationModal(false)}>
                    Cancel
                  </button>
                  <button className="btn-modal-proceed btn-modal-danger" onClick={notificationModal.onConfirm}>
                    Remove Photo
                  </button>
                </>
              ) : (
                <button className="btn-modal-proceed" onClick={() => setShowNotificationModal(false)}>
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionTableEditor;
